import React from "react";
import {
	Box,
	Typography,
	Container,
	TextField,
	Grid,
	Paper,
	Card,
	CardContent,
	CardActions,
	Button,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Divider,
} from "@mui/material";
import {
	ExpandMore,
	Search,
	HelpOutline,
	Chat,
	ErrorOutline,
} from "@mui/icons-material";

const HelpCenterDashboard = () => {
	const categories = [
		{
			title: "Getting Started",
			description: "Learn how to set up and start using our product.",
		},
		{
			title: "Account Management",
			description: "Manage your account settings and profile.",
		},
		{
			title: "Billing & Payments",
			description: "View invoices, payment methods, and billing issues.",
		},
		{
			title: "Troubleshooting",
			description: "Find solutions to common problems.",
		},
	];

	const popularArticles = [
		{ title: "How to reset your password", link: "#" },
		{ title: "Setting up two-factor authentication", link: "#" },
		{ title: "Managing your subscription", link: "#" },
	];

	const faqs = [
		{
			question: "How do I contact support?",
			answer:
				"You can chat with our support team via the chat widget or submit a ticket.",
		},
		{
			question: "How do I upgrade my plan?",
			answer:
				"Navigate to your account settings and select the subscription tab.",
		},
		{
			question: "Can I change my billing information?",
			answer:
				"Yes, you can update your billing details in your account settings.",
		},
	];

	return (
		<Box sx={{ bgcolor: "#f5f7fa", minHeight: "100vh", py: 4 }}>
			<Container maxWidth="lg">
				{/* Header */}
				<Typography variant="h3" align="center" gutterBottom sx={{ mb: 2 }}>
					Help Center
				</Typography>
				<Typography
					variant="subtitle1"
					align="center"
					color="textSecondary"
					mb={4}>
					Find answers, get support, and learn how to get the most out of our
					product.
				</Typography>

				{/* Search Bar */}
				<Box sx={{ display: "flex", justifyContent: "center", mb: 6 }}>
					<TextField
						variant="outlined"
						placeholder="Search help articles..."
						size="large"
						sx={{ width: { xs: "100%", md: "50%" } }}
						InputProps={{
							startAdornment: <Search sx={{ mr: 1, color: "gray" }} />,
						}}
					/>
				</Box>

				{/* Categories */}
				<Typography variant="h4" gutterBottom mb={3} textAlign="center">
					Popular Categories
				</Typography>
				<Grid container spacing={4} mb={6}>
					{categories.map((category, index) => (
						<Grid item xs={12} md={3} key={index}>
							<Card
								sx={{
									height: "100%",
									transition: "transform 0.2s",
									"&:hover": { transform: "scale(1.02)" },
								}}>
								<CardContent>
									<HelpOutline
										sx={{ fontSize: 40, mb: 1, color: "primary.main" }}
									/>
									<Typography variant="h6" gutterBottom>
										{category.title}
									</Typography>
									<Typography color="textSecondary">
										{category.description}
									</Typography>
								</CardContent>
								<CardActions>
									<Button size="small" color="primary">
										Explore
									</Button>
								</CardActions>
							</Card>
						</Grid>
					))}
				</Grid>

				{/* Popular Articles */}
				<Typography variant="h4" gutterBottom mb={3} textAlign="center">
					Popular Articles
				</Typography>
				<Paper sx={{ p: 2, mb: 6 }}>
					<List>
						{popularArticles.map((article, index) => (
							<React.Fragment key={index}>
								<ListItem
									button
									component="a"
									href={article.link}
									sx={{ pl: 0, pr: 0 }}>
									<ListItemIcon>
										<HelpOutline color="primary" />
									</ListItemIcon>
									<ListItemText primary={article.title} />
								</ListItem>
								{index < popularArticles.length - 1 && (
									<Divider component="li" />
								)}
							</React.Fragment>
						))}
					</List>
				</Paper>

				{/* FAQs */}
				<Typography variant="h4" gutterBottom mb={3} textAlign="center">
					Frequently Asked Questions
				</Typography>
				<Box maxWidth="800px" mx="auto">
					{faqs.map((faq, index) => (
						<Accordion key={index} sx={{ mb: 2 }}>
							<AccordionSummary expandIcon={<ExpandMore />}>
								<Typography variant="h6">{faq.question}</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Typography>{faq.answer}</Typography>
							</AccordionDetails>
						</Accordion>
					))}
				</Box>
			</Container>
		</Box>
	);
};

export default HelpCenterDashboard;
